import { useEffect } from 'react';
import { alternatesFor, urlLanguage } from '../content/publicLanguages';

// The public address. Canonical links always point here, whichever host served the page
// (upyachat.com redirects to www; staging and previews must not claim to be canonical).
export const SITE = 'https://www.upyachat.com';

const OG_LOCALE = { en: 'en_US', fr: 'fr_FR', es: 'es_ES', pt: 'pt_BR', ar: 'ar_AR', hi: 'hi_IN', ur: 'ur_PK', vi: 'vi_VN' };

const ensureMetaTag = (key, value, content) => {
  if (typeof document === 'undefined') {
    return;
  }

  let tag = document.head.querySelector(`meta[${key}="${value}"]`);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(key, value);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
};

const ensureLinkTag = (rel, href) => {
  if (typeof document === 'undefined') {
    return;
  }

  let tag = document.head.querySelector(`link[rel="${rel}"]`);

  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', href);
};

// One <link rel="alternate" hreflang> per language version of the page (and x-default), replacing
// those of the previous page (or of the pre-rendered HTML).
const replaceAlternates = (alternates) => {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((tag) => tag.remove());
  alternates.forEach(({ lang, path }) => {
    const tag = document.createElement('link');
    tag.setAttribute('rel', 'alternate');
    tag.setAttribute('hreflang', lang);
    tag.setAttribute('href', `${SITE}${path}`);
    document.head.appendChild(tag);
  });
};

const Seo = ({ title, description, canonicalPath = '/', type = 'website' }) => {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const siteTitle = title.includes('UpyaChat') ? title : `${title} | UpyaChat`;
    const canonicalUrl = `${SITE}${canonicalPath}`;

    document.title = siteTitle;

    ensureMetaTag('name', 'description', description);
    ensureMetaTag('property', 'og:title', siteTitle);
    ensureMetaTag('property', 'og:description', description);
    ensureMetaTag('property', 'og:type', type);
    ensureMetaTag('property', 'og:url', canonicalUrl);
    ensureMetaTag('property', 'og:locale', OG_LOCALE[urlLanguage(canonicalPath) || 'en']);
    ensureMetaTag('name', 'twitter:card', 'summary_large_image');
    ensureMetaTag('name', 'twitter:title', siteTitle);
    ensureMetaTag('name', 'twitter:description', description);
    ensureLinkTag('canonical', canonicalUrl);
    replaceAlternates(alternatesFor(canonicalPath));
  }, [canonicalPath, description, title, type]);

  return null;
};

export default Seo;
